<script>
/* global axios */
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import NextButton from 'dashboard/components-next/button/Button.vue';

const WAHA_STATUS = Object.freeze({
  STOPPED: 'STOPPED',
  STARTING: 'STARTING',
  SCAN_QR_CODE: 'SCAN_QR_CODE',
  WORKING: 'WORKING',
  FAILED: 'FAILED',
  UNKNOWN: 'UNKNOWN',
});

const POLL_INTERVAL_MS = 4000;
const QR_REFRESH_INTERVAL_MS = 18000; // QR expires in 20s; refresh with 2s margin

export default {
  components: { NextButton },

  setup() {
    // WAHA_STATUS must be exposed here so the template (Options API) can access it.
    // Module-level constants are not part of the component proxy in Vue 3.
    return {
      v$: useVuelidate(),
      WAHA_STATUS,
      globalConfig: useMapGetter('globalConfig/get'),
      currentAccount: useAccount().currentAccount,
    };
  },

  data() {
    return {
      step: 'form', // 'form' | 'qr'
      inboxName: '',
      createdInboxId: null,

      // QR step state
      sessionStatus: WAHA_STATUS.STARTING,
      qrImageSrc: null,
      qrLoading: false,
      pollTimer: null,
      qrTimer: null,
    };
  },

  computed: {
    uiFlags() {
      return this.$store.getters['inboxes/getUIFlags'];
    },

    accountId() {
      return this.$route.params.accountId;
    },

    isQrStep() {
      return this.step === 'qr';
    },

    // Per-account Super Admin toggle — "API Não Oficial" (WAHA) is opt-in,
    // off by default. Defense in depth: Whatsapp.vue already hides the WAHA
    // card when this is false, but a direct `?provider=waha` navigation
    // would still reach this component.
    wahaUnofficialEnabled() {
      return this.currentAccount?.waha_unofficial_enabled === true;
    },

    statusLabel() {
      const labels = {
        [WAHA_STATUS.STOPPED]: this.$t(
          'INBOX_MGMT.ADD.WHATSAPP.WAHA.STATUS.STOPPED'
        ),
        [WAHA_STATUS.STARTING]: this.$t(
          'INBOX_MGMT.ADD.WHATSAPP.WAHA.STATUS.STARTING'
        ),
        [WAHA_STATUS.SCAN_QR_CODE]: this.$t(
          'INBOX_MGMT.ADD.WHATSAPP.WAHA.STATUS.SCAN_QR_CODE'
        ),
        [WAHA_STATUS.WORKING]: this.$t(
          'INBOX_MGMT.ADD.WHATSAPP.WAHA.STATUS.WORKING'
        ),
        [WAHA_STATUS.FAILED]: this.$t(
          'INBOX_MGMT.ADD.WHATSAPP.WAHA.STATUS.FAILED'
        ),
        [WAHA_STATUS.UNKNOWN]: this.$t(
          'INBOX_MGMT.ADD.WHATSAPP.WAHA.STATUS.UNKNOWN'
        ),
      };
      return labels[this.sessionStatus] || this.sessionStatus;
    },

    showQrCode() {
      return this.sessionStatus === WAHA_STATUS.SCAN_QR_CODE && this.qrImageSrc;
    },

    showSpinner() {
      return (
        [
          WAHA_STATUS.STARTING,
          WAHA_STATUS.STOPPED,
          WAHA_STATUS.UNKNOWN,
        ].includes(this.sessionStatus) ||
        (this.sessionStatus === WAHA_STATUS.SCAN_QR_CODE && !this.qrImageSrc)
      );
    },

    showFailed() {
      return this.sessionStatus === WAHA_STATUS.FAILED;
    },
  },

  beforeUnmount() {
    this.stopPolling();
  },

  validations: {
    inboxName: { required },
  },

  methods: {
    // ── Step 1: create inbox ──────────────────────────────────────────────────

    async createChannel() {
      if (!this.wahaUnofficialEnabled) return;

      this.v$.$touch();
      if (this.v$.$invalid) return;

      try {
        const inbox = await this.$store.dispatch('inboxes/createChannel', {
          name: this.inboxName.trim(),
          channel: this.globalConfig.wahaNativeAppsEnabled
            ? {
                type: 'api',
                additional_attributes: { waha_native: true },
              }
            : {
                type: 'whatsapp',
                provider: 'waha',
                provider_config: {},
              },
        });

        this.createdInboxId = inbox.id;
        this.step = 'qr';
        this.startPolling();
      } catch (error) {
        useAlert(
          error.message ||
            this.$t('INBOX_MGMT.ADD.WHATSAPP.WAHA.API.ERROR_MESSAGE')
        );
      }
    },

    // ── Step 2: QR polling ────────────────────────────────────────────────────

    startPolling() {
      this.pollStatus();
      this.pollTimer = setInterval(this.pollStatus, POLL_INTERVAL_MS);
    },

    stopPolling() {
      clearInterval(this.pollTimer);
      clearInterval(this.qrTimer);
    },

    async pollStatus() {
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.createdInboxId}/waha_status`
        );
        const prev = this.sessionStatus;
        this.sessionStatus = data.status || WAHA_STATUS.UNKNOWN;

        if (this.sessionStatus === WAHA_STATUS.WORKING) {
          this.stopPolling();
          this.onConnected();
          return;
        }

        // Fetch QR whenever status becomes SCAN_QR_CODE (including first time and after rotation)
        if (this.sessionStatus === WAHA_STATUS.SCAN_QR_CODE) {
          if (prev !== WAHA_STATUS.SCAN_QR_CODE || !this.qrImageSrc) {
            await this.fetchQr();
            this.startQrRefreshTimer();
          }
        }
      } catch {
        // Network error — keep polling
      }
    },

    startQrRefreshTimer() {
      clearInterval(this.qrTimer);
      this.qrTimer = setInterval(async () => {
        if (this.sessionStatus === WAHA_STATUS.SCAN_QR_CODE) {
          await this.fetchQr();
        }
      }, QR_REFRESH_INTERVAL_MS);
    },

    async fetchQr() {
      this.qrLoading = true;
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.createdInboxId}/waha_qr`
        );
        const qr = data?.qr;
        if (!qr) return;

        if (qr.data && qr.mimetype) {
          // Normalised format from waha_session_service: { data: "<base64>", mimetype: "..." }
          this.qrImageSrc = `data:${qr.mimetype};base64,${qr.data}`;
        } else if (qr.value) {
          // Fallback: WAHA returned raw value field (full data URL or plain base64)
          this.qrImageSrc = qr.value.startsWith('data:')
            ? qr.value
            : `data:image/png;base64,${qr.value}`;
        }
      } catch {
        // QR might not be ready yet — will retry
      } finally {
        this.qrLoading = false;
      }
    },

    onConnected() {
      router.replace({
        name: 'settings_inboxes_add_agents',
        params: { page: 'new', inbox_id: this.createdInboxId },
      });
    },
  },
};
</script>

<template>
  <!-- ── Not available for this account ───────────────────────────────── -->
  <p v-if="!wahaUnofficialEnabled" class="text-sm text-n-slate-11">
    {{ $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.DISABLED_FOR_ACCOUNT') }}
  </p>

  <!-- ── Step 1: Form ─────────────────────────────────────────────────── -->
  <form
    v-else-if="!isQrStep"
    class="flex flex-wrap flex-col mx-0"
    @submit.prevent="createChannel()"
  >
    <p class="text-sm text-n-slate-11 mb-6">
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.DESCRIPTION') }}
    </p>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.INBOX_NAME.LABEL') }}
        <input
          v-model="inboxName"
          type="text"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.INBOX_NAME.PLACEHOLDER')
          "
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.INBOX_NAME.ERROR') }}
        </span>
      </label>
    </div>

    <div class="w-full mt-4">
      <NextButton
        :is-loading="uiFlags.isCreating"
        type="submit"
        solid
        blue
        :label="$t('INBOX_MGMT.ADD.WHATSAPP.WAHA.SUBMIT_BUTTON')"
      />
    </div>
  </form>

  <!-- ── Step 2: QR Code ──────────────────────────────────────────────── -->
  <div v-else class="flex flex-col items-center gap-6 py-4">
    <div class="text-center">
      <h2 class="text-base font-semibold text-n-slate-12 mb-1">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.QR.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.QR.SUBTITLE') }}
      </p>
    </div>

    <!-- QR Image -->
    <div
      class="w-56 h-56 flex items-center justify-center rounded-xl border border-n-weak bg-white"
    >
      <img
        v-if="showQrCode"
        :src="qrImageSrc"
        class="w-52 h-52 object-contain"
        alt="WhatsApp QR Code"
      />
      <div v-else-if="showSpinner" class="flex flex-col items-center gap-2">
        <span class="i-ri-loader-4-line animate-spin text-3xl text-n-brand" />
      </div>
      <div
        v-else-if="showFailed"
        class="flex flex-col items-center gap-2 p-4 text-center"
      >
        <span class="i-ri-error-warning-line text-3xl text-ruby-500" />
        <p class="text-xs text-ruby-600">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.QR.FAILED') }}
        </p>
      </div>
    </div>

    <!-- Status pill -->
    <span
      class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
      :class="{
        'bg-n-alpha-1 text-n-slate-11': [
          WAHA_STATUS.STARTING,
          WAHA_STATUS.STOPPED,
          WAHA_STATUS.UNKNOWN,
        ].includes(sessionStatus),
        'bg-woot-50 text-woot-700': sessionStatus === WAHA_STATUS.SCAN_QR_CODE,
        'bg-success-50 text-success-700': sessionStatus === WAHA_STATUS.WORKING,
        'bg-ruby-50 text-ruby-700': sessionStatus === WAHA_STATUS.FAILED,
      }"
    >
      <span
        class="w-1.5 h-1.5 rounded-full"
        :class="{
          'bg-n-slate-8': [
            WAHA_STATUS.STARTING,
            WAHA_STATUS.STOPPED,
            WAHA_STATUS.UNKNOWN,
          ].includes(sessionStatus),
          'bg-woot-500': sessionStatus === WAHA_STATUS.SCAN_QR_CODE,
          'bg-success-500': sessionStatus === WAHA_STATUS.WORKING,
          'bg-ruby-500': sessionStatus === WAHA_STATUS.FAILED,
        }"
      />
      {{ statusLabel }}
    </span>

    <!-- Instructions -->
    <ol
      v-if="sessionStatus === WAHA_STATUS.SCAN_QR_CODE"
      class="text-sm text-n-slate-11 space-y-1 list-decimal list-inside"
    >
      <li>{{ $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.QR.STEP_1') }}</li>
      <li>{{ $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.QR.STEP_2') }}</li>
      <li>{{ $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.QR.STEP_3') }}</li>
    </ol>
  </div>
</template>
